/**
 * Gate-value freshness signal shared by the analytics provider and
 * `useFeatureGate`. `markProviderReady` (index.ts) only fires once, after the
 * initial Statsig fetch, but gate values change again whenever the user is
 * identified after sign-in. Without this store a gated page that rendered
 * during the anonymous window kept its stale `false` until a refresh.
 *
 * - `pending` counts in-flight identity updates and `latestUserId` is the
 *   user the newest one targets. Consumers treat the values as still loading
 *   only while those updates move to a user whose values have not settled
 *   (sign-in, sign-out, account switch), so they never commit to another
 *   user's evaluation. Re-identifying the settled user after a wallet or
 *   profile change does not count: Statsig keys its cache by user ID and
 *   custom IDs, so it keeps serving that user's values.
 * - `latestHasValues` records whether the newest update started with values
 *   to serve. Statsig empties its store when an update starts and refills it
 *   from that user's cache, so a first sign-in on a device serves no values
 *   until the fetch settles; every gate reads as loading in that window.
 * - `servedGateValues` keeps the last value each gate served from settled
 *   values, so a page that already shows a gate keeps it through that window
 *   instead of dropping to a loading state and remounting. It is cleared when
 *   the last update settles: mounted gates read again and record fresh values,
 *   and a gate that nothing reads cannot keep a value from older values.
 * - `version` bumps when the last in-flight update settles so subscribers
 *   re-read gates. A superseded update can settle first, while the store
 *   still waits for the newer one, so it does not bump the version.
 */
let version = 0;
let pending = 0;
// The provider initializes anonymously (`userID: ''`).
let settledUserId = '';
let latestUserId = '';
let latestHasValues = true;
const servedGateValues: Map<string, ServedGateValue> = new Map();
const listeners: Set<() => void> = new Set();

interface ServedGateValue {
  value: boolean;
  // The user whose values the gate served, so a user-scoped gate never shows
  // another account's value.
  userId: string;
}

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Marks the start of a provider identity update for a user.
 *
 * @param userId - The user the update targets.
 * @param hasValues - Whether the provider already serves values for that user
 * from its cache. Read it after the update starts: Statsig empties its store
 * and loads the user's cache synchronously when the update begins.
 *
 * @example
 * ```ts
 * const update = client.updateUserAsync(user);
 * beginGateValuesUpdate(user.userID, client.getContextHandle().values !== null);
 * update.finally(endGateValuesUpdate);
 * ```
 */
export function beginGateValuesUpdate(userId: string, hasValues: boolean): void {
  pending += 1;
  latestUserId = userId;
  latestHasValues = hasValues;
  notify();
}

/**
 * Marks one identity update as settled, whether it succeeded or failed.
 *
 * @remarks
 * When the last in-flight update settles, this records its user as settled,
 * clears the served gate values and bumps the version, so subscribers read
 * their gates again and record fresh values. A superseded update can settle
 * while a newer one is still in flight; that changes only the pending state,
 * because the store still waits for the newer update.
 */
export function endGateValuesUpdate(): void {
  pending = Math.max(0, pending - 1);
  if (pending === 0) {
    settledUserId = latestUserId;
    servedGateValues.clear();
    version += 1;
  }

  notify();
}

export function subscribeGateValues(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

export function getGateValuesVersion(): number {
  return version;
}

/** `true` while an identity update targets a user whose values have not settled. */
export function getGateValuesPending(): boolean {
  return pending > 0 && latestUserId !== settledUserId;
}

/**
 * Returns `true` while an identity update is in flight and the provider has no
 * values to serve, as on a first sign-in on a device.
 *
 * @example
 * ```ts
 * const valuesMissing = useSyncExternalStore(subscribeGateValues, getGateValuesMissing, () => false);
 * ```
 */
export function getGateValuesMissing(): boolean {
  return pending > 0 && !latestHasValues;
}

/**
 * Records the value a gate served from settled values, for the user whose
 * values the provider serves now.
 *
 * @param name - The gate's key in `featureGates`.
 * @param value - The value the gate served.
 *
 * @remarks
 * Writes module state. `useFeatureGate` calls it while it renders, right after
 * it reads the gate; the next settled update clears the record.
 *
 * @example
 * ```ts
 * const value = checkGate(name);
 * rememberServedGateValue(name, value);
 * ```
 */
export function rememberServedGateValue(name: string, value: boolean): void {
  servedGateValues.set(name, { value, userId: latestUserId });
}

/**
 * Returns the value a gate last served from settled values.
 *
 * @param name - The gate's key in `featureGates`.
 * @param userScoped - Whether the gate is evaluated per user. A user-scoped
 * gate returns only a value served for the user of the newest update.
 * @returns The served value, or `undefined` when the gate has served no value
 * that applies.
 *
 * @example
 * ```ts
 * const userScoped = featureGates[name].idType !== 'stableID';
 * const kept = getServedGateValue(name, userScoped) ?? null;
 * ```
 */
export function getServedGateValue(name: string, userScoped: boolean): boolean | undefined {
  const served = servedGateValues.get(name);
  if (served === undefined) return undefined;
  if (userScoped && served.userId !== latestUserId) return undefined;

  return served.value;
}
